import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, safeHtml } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { PageShell, ProductVisual } from "@/components/site/Layout";
import { SITE, useJsonLd } from "@/components/site/Seo";

const ORG_LD = {
  "@context": "https://schema.org",
  "@graph": [
    { "@type": "Organization", "@id": `${SITE}/#org`, name: "HONEY NUTS", url: `${SITE}/`, logo: `${SITE}/honey-nuts-logo.png` },
    { "@type": "WebSite", "@id": `${SITE}/#website`, name: "HONEY NUTS", url: `${SITE}/`, publisher: { "@id": `${SITE}/#org` } },
  ],
};
import { WaitlistForm } from "@/pages/store/LaunchList";

const IDEAS = [
  { img: "/images/yogurt_granola_bowl.jpg", alt: "Bowl of yoghurt topped with granola (illustrative stock photo)", title: "Yoghurt", text: "A spoonful over thick yoghurt." },
  { img: "/images/breakfast_porridge_bowl.jpg", alt: "Bowl of warm porridge (illustrative stock photo)", title: "Porridge", text: "Stirred through warm oats." },
  { img: null, title: "Toast", text: "Spread over buttered toast." },
];

export default function Home() {
  const { config } = useStore();
  useJsonLd("org", ORG_LD);
  const [product, setProduct] = useState(null);
  const [about, setAbout] = useState(null);
  useEffect(() => {
    api.get("/products/honey-nuts-750g").then((r) => setProduct(r.data)).catch(() => {});
    api.get("/content/page/about").then((r) => setAbout(r.data)).catch(() => {});
  }, []);
  const live = config?.mode === "LIVE";
  const img = product?.images?.[0];
  return (
    <>
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-14 md:pt-24 pb-16 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6 fade-up">
          <p className="eyebrow mb-5">{live ? "Now available" : "Coming soon"}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#173B2D]" data-testid="home-headline">Something delicious is coming to your breakfast.</h1>
          <p className="mt-6 text-base md:text-lg text-[#3d5248] max-w-md">Honey with mixed nuts and seeds. A spoonful for yoghurt, porridge or toast.</p>
          <div className="mt-9 flex flex-wrap gap-3">
            {live ? <Link to="/shop" className="hn-btn" data-testid="hero-shop-btn">Shop Honey Nuts</Link>
              : <a href="#launch-list" className="hn-btn" data-testid="hero-join-launch-btn">Join the launch list</a>}
            <Link to="/shop" className="hn-btn hn-btn-outline" data-testid="hero-product-btn">See the product</Link>
          </div>
          <dl className="mt-12 grid grid-cols-3 gap-4 border-t border-[#E5DEC9] pt-6" data-testid="hero-highlights">
            {[["60%", "honey"], ["40%", "nuts and seeds"], ["750 g", "net weight"]].map(([v, l]) => (
              <div key={l}><dt className="serif text-3xl md:text-4xl text-[#A66B24]">{v}</dt><dd className="text-xs md:text-sm text-[#3d5248] mt-1">{l}</dd></div>
            ))}
          </dl>
        </div>
        <div className="md:col-span-6">
          <ProductVisual image={img ? { src: img.url.startsWith("/api") ? process.env.REACT_APP_BACKEND_URL + img.url : img.url, alt: img.alt, is_concept: img.is_concept } : null} className="aspect-[4/5] w-full" />
        </div>
      </section>

      <section className="bg-[#EFE8D2]/60 border-y border-[#E5DEC9]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-20">
          <p className="eyebrow mb-3">Serving ideas</p>
          <h2 className="text-3xl md:text-4xl mb-10">One jar, three breakfasts</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {IDEAS.map((i) => (
              <article key={i.title} className="hn-card overflow-hidden">
                {i.img ? <img src={i.img} alt={i.alt} width="800" height="600" className="aspect-[4/3] w-full object-cover" loading="lazy" decoding="async" />
                  : <div className="aspect-[4/3] honeycomb-bg" aria-hidden />}
                <div className="p-5"><h3 className="text-2xl">{i.title}</h3><p className="text-sm text-[#3d5248] mt-1">{i.text}</p></div>
              </article>
            ))}
          </div>
          <p className="text-xs text-[#5b6b62] mt-4">Serving photos are illustrative stock images, not photos of HONEY NUTS.</p>
        </div>
      </section>

      {product && (
        <section className="max-w-6xl mx-auto px-5 md:px-8 py-20 grid md:grid-cols-2 gap-12">
          <div>
            <p className="eyebrow mb-3">What's inside</p>
            <h2 className="text-3xl md:text-4xl mb-6">Eight ingredients, nothing else</h2>
            <ul className="divide-y divide-[#E5DEC9] border-y border-[#E5DEC9]" data-testid="home-ingredients">
              {product.ingredients.map((i) => (
                <li key={i.name} className="flex justify-between py-3 text-sm">
                  <span className={i.allergen ? "font-semibold" : ""}>{i.name}</span><span className="tabular-nums">{i.pct}%</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm font-semibold border-l-4 border-[#A66B24] pl-3" data-testid="home-allergen-statement">{product.allergen_statement}</p>
          </div>
          <div className="hn-card p-8 flex flex-col justify-between">
            <div>
              <p className="eyebrow mb-3">The jar</p>
              <h3 className="text-3xl">{product.name}</h3>
              <p className="text-sm text-[#3d5248] mt-2">{product.subtitle} · {product.net_weight} net</p>
              <p className="serif text-4xl mt-6" data-testid="home-price">{(product.price_cents / 100).toLocaleString("en-AU", { style: "currency", currency: "AUD" })}</p>
              <p className="text-xs text-[#5b6b62]">Proposed price, plus delivery</p>
            </div>
            <Link to="/shop" className="hn-btn mt-8 self-start" data-testid="home-view-product-btn">{live ? "Shop Honey Nuts" : "View product details"}</Link>
          </div>
        </section>
      )}

      {about && (
        <section className="max-w-3xl mx-auto px-5 md:px-8 py-10 text-center">
          <p className="eyebrow mb-3">About</p>
          <div className="prose-hn text-lg" dangerouslySetInnerHTML={safeHtml(about.body_html)} />
          <Link to="/about" className="text-sm underline text-[#A66B24]">Read more</Link>
        </section>
      )}

      {!live && (
        <PageShell narrow>
          <div id="launch-list" className="hn-card p-8 md:p-12 scroll-mt-28">
            <p className="eyebrow mb-3">Launch list</p>
            <h2 className="text-3xl md:text-4xl mb-2">Be first to know</h2>
            <p className="text-sm text-[#3d5248] mb-8">We'll email you once when HONEY NUTS is available to order. No preorders, no payment.</p>
            <WaitlistForm source="home" />
          </div>
        </PageShell>
      )}
    </>
  );
}
